import React, { useCallback } from 'react';
import { Input, Checkbox } from 'antd';
import { BulkWearableItem, PricingDetail, PricingOption } from './types';

interface BulkPricingStepProps {
  styles: { [key: string]: string };
  bulkItems: BulkWearableItem[];
  setBulkItems: React.Dispatch<React.SetStateAction<BulkWearableItem[]>>;
  pricingOptions: PricingOption[];
}

const BulkPricingStep: React.FC<BulkPricingStepProps> = ({ styles, bulkItems, setBulkItems, pricingOptions }) => {
  const handleBulkPricingToggle = useCallback((index: number, values: string[]) => {
    const freeOptions = ['free', 'freemium'];
    const paidOptions = ['renting', 'purchasing', 'payPerUse'];
    
    // Check if both free and paid options are selected
    const selectedFree = values.some(v => freeOptions.includes(v));
    const selectedPaid = values.some(v => paidOptions.includes(v));
    
    // If both types are selected, implement mutual exclusivity
    let finalValues = values;
    if (selectedFree && selectedPaid) {
      const currentItem = bulkItems[index];
      const previousFree = currentItem.pricingModel.some(v => freeOptions.includes(v));
      const previousPaid = currentItem.pricingModel.some(v => paidOptions.includes(v));
      
      // If user just selected a free option, remove all paid options
      if (!previousFree && selectedFree) {
        finalValues = values.filter(v => freeOptions.includes(v));
      } 
      // If user just selected a paid option, remove all free options
      else if (!previousPaid && selectedPaid) {
        finalValues = values.filter(v => paidOptions.includes(v));
      }
    }
    
    setBulkItems(prev => {
      const updated = [...prev];
      const newDetails = { ...updated[index].pricingDetails };
      finalValues.forEach(v => {
        if (!newDetails[v]) {
          newDetails[v] = { price: 0, description: '' };
        }
      });
      Object.keys(newDetails).forEach(k => {
        if (!finalValues.includes(k)) {
          delete newDetails[k];
        }
      });
      updated[index] = { ...updated[index], pricingModel: finalValues, pricingDetails: newDetails };
      return updated;
    });
  }, [setBulkItems, bulkItems]);

  const updateBulkPricingDetail = useCallback((index: number, model: string, field: keyof PricingDetail, value: any) => {
    setBulkItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        pricingDetails: {
          ...updated[index].pricingDetails,
          [model]: {
            ...updated[index].pricingDetails[model],
            [field]: value
          }
        }
      };
      return updated;
    });
  }, [setBulkItems]);

  return (
    <div className={styles.stepContainer}>
      <h2 className={styles.stepTitle}>Pricing and Interfaces</h2>

      {bulkItems.map((item, index) => {
        const displayName = item.itemName || `Item ${index + 1}`;

        return (
          <div key={`pricing-${item.itemId}-${index}`}>
            <div className={styles.bulkPricingCard}>
              <div className={styles.bulkPricingHeader}>
                {item.image && (
                  <img src={item.image} alt={displayName} className={styles.bulkPricingImage} />
                )}
                <div>
                  <span className={styles.bulkPricingFileLabel}>File Name</span>
                  <strong className={styles.bulkPricingName}>{displayName}</strong>
                </div>
              </div>

              <div className={styles.pricingOptions}>
                <Checkbox.Group
                  className={styles.radioGroup}
                  value={item.pricingModel}
                  onChange={(values) => handleBulkPricingToggle(index, values as string[])}
                >
                  {pricingOptions.map(opt => (
                    <div key={opt.value} className={styles.pricingOptionWrapper}>
                      <div className={`${styles.pricingOption} ${item.pricingModel.includes(opt.value) ? styles.pricingOptionSelected : ''} ${item.pricingModel.includes(opt.value) && opt.value !== 'free' && opt.value !== 'freemium' ? styles.pricingOptionWithSub : ''}`}>
                        <Checkbox value={opt.value} className={styles.radioOption}>
                          <div className={styles.optionContent}>
                            <strong>{opt.label}</strong>
                            <p>{opt.desc}</p>
                          </div>
                        </Checkbox>
                      </div>
                      {item.pricingModel.includes(opt.value) && opt.value !== 'free' && opt.value !== 'freemium' && (
                        <div className={styles.pricingSubFields}>
                          <div className={styles.pricingSubField}>
                            <label>Price <span style={{ color: 'red' }}>*</span></label>
                            <Input
                              type="number"
                              onWheel={(e: any) => e.target.blur()}
                              min={0}
                              placeholder="Enter price in USD"
                              className={styles.textInput}
                              status={!item.pricingDetails[opt.value]?.price ? 'error' : undefined}
                              value={item.pricingDetails[opt.value]?.price || ''}
                              onChange={(e) => updateBulkPricingDetail(index, opt.value, 'price', parseFloat(e.target.value) || 0)}
                            />
                          </div>
                          <div className={styles.pricingSubField}>
                            <label>Description</label>
                            <Input.TextArea
                              rows={4}
                              placeholder="Enter pricing description"
                              className={styles.textInput}
                              value={item.pricingDetails[opt.value]?.description || ''}
                              onChange={(e) => updateBulkPricingDetail(index, opt.value, 'description', e.target.value)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </Checkbox.Group>
              </div>
            </div>

            {index < bulkItems.length - 1 && <hr className={styles.bulkPricingDivider} />}
          </div>
        );
      })}
    </div>
  );
};

export default BulkPricingStep;
